import {
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  Select,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';
import { CreditFields, Credit } from '../../models/components/schemas/credit/Credit';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoCreditsFields = readonly CreditFields[];

/** Options for GET /videos/{video_id}/credits */
export type GetVideoCreditsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.credit+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id}/credits */
  query?: {
    /**
     * The sort direction of the results.
     *
     * Option descriptions:
     *  * `asc` - Sort the results in ascending order.
     *  * `desc` - Sort the results in descending order.
     *
     */
    direction?: `asc` | `desc`;

    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The number of items to show on each page of results, up to a maximum of 100.
     */
    perPage?: number;

    /**
     * The search query to use to filter the results.
     */
    query?: string;

    /**
     * The way to sort the results.
     *
     * Option descriptions:
     *  * `alphabetical` - Sort the results alphabetically.
     *  * `date` - Sort the results by date.
     *
     */
    sort?: `alphabetical` | `date`;
  };
};

/** Result type for GET /videos/{video_id}/credits */
export type GetVideoCreditsResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /videos/{video_id}/credits */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<Credit, Paths>[];
};

/**
 * Get all the credited users in a video
 * This method returns every credited user in a video.
 */
export async function getVideoCredits<Paths extends GetVideoCreditsFields>({
  baseUrl,
  select,
  where: { videoId },
  query,
  ...options
}: GetVideoCreditsOptions<Paths>): Promise<GetVideoCreditsResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/credits?${searchQueryString(query)}&fields=${select
      .map(intoSnakeCase)
      .join(',')}`,
    {
      ...options,
      method: 'GET',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as GetVideoCreditsResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type PostVideoCreditsFields = readonly CreditFields[];

/** Options for POST /videos/{video_id}/credits */
export type PostVideoCreditsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.credit+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Data serialized to body for POST /videos/{video_id}/credits */
  variables: {
    /**
     * The email address of the credited person.
     * @example user@example.com
     */

    email: string;

    /**
     * The name of the credited person.
     * @example Sam Doe
     */

    name: string;

    /**
     * The role of the credited person.
     * @example Producer
     */

    role: string;

    /**
     * The Vimeo URI of the credited person.
     * @example /users/152184
     */

    userUri: string;
  };
};

/** Result type for POST /videos/{video_id}/credits */
export type PostVideoCreditsResult<Paths extends readonly string[]> = Select<Credit, Paths>;

/**
 * Credit a user in a video
 * This method adds a user credit to the specified video.
 */
export async function postVideoCredits<Paths extends PostVideoCreditsFields>({
  baseUrl,
  select,
  variables,
  where: { videoId },
  ...options
}: PostVideoCreditsOptions<Paths>): Promise<PostVideoCreditsResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/credits?fields=${select.map(intoSnakeCase).join(',')}`,
    {
      ...options,
      method: 'POST',
      body: JSON.stringify(deepSnakeCase(variables)),
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as PostVideoCreditsResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}
